import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useFilters } from "@/context/FilterContext";
import TrendChart from "@/components/dashboard/TrendChart";
import Heatmap from "@/components/dashboard/Heatmap";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function Panel({ n, title, right, children, className = "" }) {
  return (
    <div className={`bg-white p-6 rounded-xl border border-slate-200/60 kpi-shadow ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <span className="text-[#93c5fd] mr-2 font-black text-lg">{n}.</span>
          <span className="text-[#1e40af] font-bold text-sm uppercase tracking-tight">{title}</span>
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

export default function TrendPage() {
  const { params } = useFilters();
  const [gran, setGran] = useState("auto");
  const [trend, setTrend] = useState({ items: [], granularity: "daily" });
  const [heat, setHeat] = useState({ days: [], bins: [], grid: [] });

  useEffect(() => {
    (async () => {
      const [t, h] = await Promise.all([
        api.get("/metrics/trend", { params: { ...params, granularity: gran === "auto" ? undefined : gran } }),
        api.get("/metrics/heatmap", { params }),
      ]);
      setTrend(t.data); setHeat(h.data);
    })();
  }, [params.start, params.end, params.line, params.subsystem, gran]);

  const totalEvents = trend.items.reduce((s, i) => s + i.events, 0);
  const totalDowntime = trend.items.reduce((s, i) => s + i.downtime_min, 0);
  const avgEvents = trend.items.length > 0 ? totalEvents / trend.items.length : 0;

  return (
    <div className="space-y-6 pb-8" data-testid="detail-trend">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          ["Buckets", trend.items.length, "text-[#1e40af]"],
          ["Total Events", totalEvents.toLocaleString(), "text-slate-900"],
          ["Total Downtime (min)", Math.round(totalDowntime).toLocaleString(), "text-[#f59e0b]"],
          ["Avg Events / Bucket", avgEvents.toFixed(1), "text-[#22c55e]"],
        ].map(([label, v, cls]) => (
          <div key={label} className="bg-white p-5 rounded-xl border border-slate-200/60 kpi-shadow">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
            <div className={`text-3xl font-black mt-1 ${cls}`}>{v}</div>
          </div>
        ))}
      </div>

      <Panel
        n="1"
        title={`Trend Analysis (${trend.granularity})`}
        right={
          <Select value={gran} onValueChange={setGran}>
            <SelectTrigger className="w-32 h-8 text-xs" data-testid="trend-gran"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto</SelectItem>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
        }
      >
        <TrendChart items={trend.items} granularity={trend.granularity} />
      </Panel>

      <Panel n="2" title="Failure Density Heatmap (Day × Hour)">
        <Heatmap {...heat} />
      </Panel>
    </div>
  );
}
